import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import Axios from "axios";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";

const MAX_VIDEO_MB = 500;
const MAX_IMAGE_MB = 3;

function App() {
  const [imageFilelist, setImageFilelist] = useState([]);
  const [videoFilelist, setVideoFilelist] = useState([]);

  const [password, setPassword] = useState("");
  const [isPasswordSet, setIsPasswordSet] = useState(false);
  const [activeTab, setActiveTab] = useState("image");

  // UI state: บอกว่าอัปอะไรอยู่
  const [uploading, setUploading] = useState(false);
  const [uploadItems, setUploadItems] = useState([]); // [{id,name,type,status,progress,error}]

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => onDrop(acceptedFiles),
  });

  useEffect(() => {
    const savedPassword = localStorage.getItem("userPassword");
    if (savedPassword) setIsPasswordSet(true);

    const savedImageFilelist = JSON.parse(localStorage.getItem("imageFilelist")) || [];
    const savedVideoFilelist = JSON.parse(localStorage.getItem("videoFilelist")) || [];
    setImageFilelist(savedImageFilelist);
    setVideoFilelist(savedVideoFilelist);
  }, []);

  useEffect(() => {
    localStorage.setItem("imageFilelist", JSON.stringify(imageFilelist));
    localStorage.setItem("videoFilelist", JSON.stringify(videoFilelist));
  }, [imageFilelist, videoFilelist]);

  const getKind = (file) => {
    // แยกตาม mime / นามสกุลให้ชัด (บางที browser ส่ง gif เป็น image/gif)
    const isGif = file.type === "image/gif" || file.name.toLowerCase().endsWith(".gif");
    const isJpgPng = ["image/jpeg", "image/png"].includes(file.type);
    const isVideo = ["video/mp4", "video/x-msvideo", "video/avi"].includes(file.type) ||
      /\.(mp4|avi)$/i.test(file.name);

    if (isGif) return "gif";
    if (isJpgPng) return "image";
    if (isVideo) return "video";
    return "unknown";
  };

  const bytesToMB = (b) => b / (1024 * 1024);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(async (file) => {
      const isVideo = file.type.startsWith("video/");
      const isGif = file.type === "image/gif";
      const isImage = ["image/jpeg", "image/png", "image/webp"].includes(file.type);

      // size check
      if (isVideo && file.size > 500 * 1024 * 1024) {
        Swal.fire("ไฟล์วิดีโอเกิน 500MB");
        return;
      }
      if ((isImage || isGif) && file.size > 3 * 1024 * 1024) {
        Swal.fire("ไฟล์รูป/GIF เกิน 3MB");
        return;
      }

      let uploadFile = file;
      let uploadType = "";

      if (isVideo) {
        uploadType = "video";
        uploadFile = await convertVideoToH264(file); // mp4 h264
      } else if (isGif) {
        uploadType = "gif";
        // ไม่แปลง
      } else if (isImage) {
        uploadType = "image";
        uploadFile = await convertImageToWebP(file);
      } else {
        Swal.fire("ไม่รองรับไฟล์นี้");
        return;
      }

      const filename = `${uuidv4()}.${uploadType === "video" ? "mp4" : uploadType === "gif" ? "gif" : "webp"}`;

      const link = await uploadToWorker(uploadFile, filename, uploadType);
      addToFileList(uploadFile, link, uploadType === "video" ? "video" : "image");



    });
  }, []);


  const getExtFromFile = (file, fallback) => {
    const name = file?.name || "";
    const m = name.match(/\.([a-z0-9]+)$/i);
    return (m?.[1] || fallback).toLowerCase();
  };

  // ============ Convert ============

  // ⚠️ Placeholder: การแปลง H264 ใน browser ต้องใช้ ffmpeg.wasm หรือทำบน server
  const convertVideoToH264 = async (file) => {
    // ตอนนี้คืนไฟล์เดิมไว้ก่อน แต่ “อย่าทำให้เป็น webp”
    // ถ้าจะทำจริง แนะนำให้ทำที่ Worker/Server แล้วส่งกลับมาเป็น mp4(h264)
    return file;
  };

  const convertImageToWebP = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error("WebP conversion failed"));
              const webpFile = new File([blob], generateFileName("webp"), {
                type: "image/webp",
              });
              resolve(webpFile);
            },
            "image/webp",
            0.85 // quality
          );
        };
        img.onerror = () => reject(new Error("Invalid image"));
        img.src = reader.result;
      };

      reader.onerror = () => reject(new Error("Read file failed"));
      reader.readAsDataURL(file);
    });
  };

  const generateFileName = (extension) => `${uuidv4()}.${extension}`;

  // ============ Upload ============

  const detectType = (file) => {
    const name = (file.name || "").toLowerCase();
    const mime = (file.type || "").toLowerCase();

    if (mime === "image/gif" || name.endsWith(".gif")) return "gif";
    if (mime.startsWith("video/") || name.endsWith(".mp4") || name.endsWith(".avi")) return "video";
    return "image";
  };

  const uploadToWorker = async (file, filename, type, onProgress) => {
    const formData = new FormData();
    formData.append("System", "javisauto");
    formData.append("filename", filename); // ✅ เพิ่ม
    formData.append("file", new File([file], filename, { type: file.type }));

    const res = await Axios.post(
      `https://r2-public.wordbalance931.workers.dev/upload/${type}`, // ✅ แก้ path
      formData,
      {
        headers: { Authorization: "Bearer langter" }, // ✅ ต้องตรงกับ UPLOAD_TOKEN
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          onProgress?.(Math.round((evt.loaded * 100) / evt.total));
        },
      }
    );

    return res.data.link;
  };



const addToFileList = (file, link, type) => {
  const item = { file: link, link };
  if (type === "image") setImageFilelist((prev) => [item, ...prev]);
  else setVideoFilelist((prev) => [item, ...prev]);
};


  // ============ Auth / UI ============

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === "Xx5D1Od12F") {
      localStorage.setItem("userPassword", password);
      setIsPasswordSet(true);
    } else {
      Swal.fire({
        icon: "error",
        title: "รหัสผ่านไม่ถูกต้อง",
        text: "กรุณาตรวจสอบรหัสผ่านอีกครั้ง",
      });
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      Swal.fire({ icon: "success", title: "คัดลอกลิงก์เรียบร้อย" });
    });
  };

  const kindLabel = (k) => {
    if (k === "video") return "วิดีโอ (MP4/AVI → H264)";
    if (k === "image") return "รูปภาพ (JPG/PNG → WebP)";
    if (k === "gif") return "GIF (ส่งเดิม)";
    return "ไม่ทราบชนิด";
  };

  return (
    <div className="upload-container">
      {!isPasswordSet && (
        <div className="password-modal">
          <h2>กรุณาใส่รหัสผ่าน</h2>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="กรอกรหัสผ่าน"
            />
            <button type="submit">ยืนยัน</button>
          </form>
        </div>
      )}

      {isPasswordSet && (
        <>
          <div {...getRootProps()} className="dropzone-container">
            <input {...getInputProps()} />
            <p>
              ลากวาง/คลิกเพื่ออัปโหลด <br />
              • วิดีโอ MP4/AVI (≤ {MAX_VIDEO_MB}MB) → H264 <br />
              • JPG/PNG (≤ {MAX_IMAGE_MB}MB) → WebP <br />
              • GIF (≤ {MAX_IMAGE_MB}MB) → ส่งเดิม
            </p>
          </div>

          {/* ✅ Upload status UI */}
          {!!uploadItems.length && (
            <div style={{ marginTop: 12 }}>
              <h4 style={{ marginBottom: 8 }}>
                สถานะอัปโหลด {uploading ? "(กำลังอัปโหลด...)" : ""}
              </h4>
              <div style={{ display: "grid", gap: 8 }}>
                {uploadItems.slice(0, 8).map((it) => (
                  <div key={it.id} style={{ border: "1px solid #ddd", padding: 10, borderRadius: 8 }}>
                    <div style={{ fontWeight: 600 }}>{it.name}</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{kindLabel(it.kind)}</div>
                    <div style={{ fontSize: 12, marginTop: 6 }}>
                      {it.status === "uploading" && `กำลังอัปโหลด... ${it.progress}%`}
                      {it.status === "done" && "เสร็จแล้ว ✅"}
                      {it.status === "error" && `ผิดพลาด ❌: ${it.error}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="tabs" style={{ marginTop: 16 }}>
            <button onClick={() => setActiveTab("image")}>รูปภาพ</button>
            <button onClick={() => setActiveTab("video")}>วิดีโอ</button>
          </div>

          <div className="file-list">
            {activeTab === "image" && (
              <div className="image-list">
                <h1>รายการรูปภาพ / GIF</h1>
                <table className="file-table">
                  <thead>
                    <tr>
                      <th>ภาพ</th>
                      <th>ลิงก์ไฟล์</th>
                      <th>คัดลอก</th>
                      <th>ดาวน์โหลด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imageFilelist.map((image, index) => (
                      <tr key={index}>
                        <td>
                          {/* ถ้าเป็น gif ก็แสดงได้เหมือนรูป */}
                          <img src={image.file} alt="Uploaded" className="image-preview" />
                        </td>
                        <td>
                          <a href={image.link} target="_blank" rel="noopener noreferrer">
                            {image.link}
                          </a>
                        </td>
                        <td>
                          <button onClick={() => handleCopyLink(image.link)}>คัดลอกลิงก์</button>
                        </td>
                        <td>
                          <a href={image.link} download>
                            <button>ดาวน์โหลด</button>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "video" && (
              <div className="video-list">
                <h1>รายการวิดีโอ</h1>
                <table className="file-table">
                  <thead>
                    <tr>
                      <th>วิดีโอ</th>
                      <th>ลิงก์ไฟล์</th>
                      <th>คัดลอก</th>
                      <th>ดาวน์โหลด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {videoFilelist.map((video, index) => (
                      <tr key={index}>
                        <td>
                          <video width="160" height="auto" controls>
                            <source src={video.file} type="video/mp4" />
                          </video>
                        </td>
                        <td>
                          <a href={video.link} target="_blank" rel="noopener noreferrer">
                            {video.link}
                          </a>
                        </td>
                        <td>
                          <button onClick={() => handleCopyLink(video.link)}>คัดลอกลิงก์</button>
                        </td>
                        <td>
                          <a href={video.link} download>
                            <button>ดาวน์โหลด</button>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default App;
